"use client"

import * as React from "react"
import * as SheetPrimitive from "@radix-ui/react-dialog"
import { cva, type VariantProps } from "class-variance-authority"
import { X } from "lucide-react"

import { cn } from "@/lib/utils"

/**
 * Sheet root component that controls the sheet state.
 * Use with SheetTrigger, SheetContent, and other sheet sub-components.
 */
const Sheet = SheetPrimitive.Root

/**
 * SheetTrigger component that opens the sheet when clicked.
 * Wraps the element that should trigger the sheet.
 */
const SheetTrigger = SheetPrimitive.Trigger

/**
 * SheetClose component that closes the sheet when clicked.
 */
const SheetClose = SheetPrimitive.Close

/**
 * SheetPortal component that renders the sheet in a portal.
 */
const SheetPortal = SheetPrimitive.Portal

/**
 * SheetOverlay component displays the backdrop behind the sheet.
 * Built on Radix UI Dialog primitive with dark overlay.
 * 
 * @component
 * @param {React.ComponentPropsWithoutRef<typeof SheetPrimitive.Overlay>} props - Overlay props
 * @param {string} [props.className] - Additional CSS classes
 * @returns {JSX.Element} Sheet overlay backdrop
 */
const SheetOverlay = React.forwardRef<
  React.ElementRef<typeof SheetPrimitive.Overlay>,
  React.ComponentPropsWithoutRef<typeof SheetPrimitive.Overlay>
>(({ className, ...props }, ref) => (
  <SheetPrimitive.Overlay
    className={cn(
      "fixed inset-0 z-50 bg-black/80  data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0",
      className
    )}
    {...props}
    ref={ref}
  />
))
SheetOverlay.displayName = SheetPrimitive.Overlay.displayName

const sheetVariants = cva(
  "fixed z-50 gap-4 bg-background p-6 shadow-lg transition ease-in-out data-[state=closed]:duration-300 data-[state=open]:duration-500 data-[state=open]:animate-in data-[state=closed]:animate-out",
  {
    variants: {
      side: {
        top: "inset-x-0 top-0 border-b data-[state=closed]:slide-out-to-top data-[state=open]:slide-in-from-top",
        bottom:
          "inset-x-0 bottom-0 border-t data-[state=closed]:slide-out-to-bottom data-[state=open]:slide-in-from-bottom",
        left: "inset-y-0 left-0 h-full w-3/4 border-r data-[state=closed]:slide-out-to-left data-[state=open]:slide-in-from-left sm:max-w-sm",
        right:
          "inset-y-0 right-0 h-full w-3/4 border-l data-[state=closed]:slide-out-to-right data-[state=open]:slide-in-from-right sm:max-w-sm",
      },
    },
    defaultVariants: {
      side: "right",
    },
  }
)

/**
 * Props for SheetContent component
 */
interface SheetContentProps
  extends React.ComponentPropsWithoutRef<typeof SheetPrimitive.Content>,
    VariantProps<typeof sheetVariants> {}

/**
 * SheetContent component displays the sheet content panel.
 * Supports side variants (top, bottom, left, right) with slide animations.
 * Built on Radix UI Dialog primitive.
 * 
 * @component
 * @param {SheetContentProps} props - Content props
 * @param {"top" | "bottom" | "left" | "right"} [props.side="right"] - Side to slide from
 * @param {React.ReactNode} props.children - Content to display
 * @param {string} [props.className] - Additional CSS classes
 * @returns {JSX.Element} Sheet content panel with animations
 * 
 * @example
 * ```tsx
 * <Sheet>
 *   <SheetTrigger>Open</SheetTrigger>
 *   <SheetContent side="right">
 *     <SheetHeader>
 *       <SheetTitle>Title</SheetTitle>
 *     </SheetHeader>
 *     <p>Content</p>
 *   </SheetContent>
 * </Sheet>
 * ```
 */
const SheetContent = React.forwardRef<
  React.ElementRef<typeof SheetPrimitive.Content>,
  SheetContentProps
>(({ side = "right", className, children, ...props }, ref) => (
  <SheetPortal>
    <SheetOverlay />
    <SheetPrimitive.Content
      ref={ref}
      className={cn(sheetVariants({ side }), className)}
      {...props}
    >
      <SheetPrimitive.Close className="absolute right-4 top-4 rounded-sm opacity-70 ring-offset-background transition-opacity hover:opacity-100 focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2 disabled:pointer-events-none data-[state=open]:bg-secondary">
        <X className="h-4 w-4" />
        <span className="sr-only">Close</span>
      </SheetPrimitive.Close>
      {children}
    </SheetPrimitive.Content>
  </SheetPortal>
))
SheetContent.displayName = SheetPrimitive.Content.displayName

/**
 * SheetHeader component provides a container for sheet title and description.
 * 
 * @component
 * @param {React.HTMLAttributes<HTMLDivElement>} props - Standard div element props
 * @param {string} [props.className] - Additional CSS classes
 * @returns {JSX.Element} Sheet header container
 */
const SheetHeader = ({
  className,
  ...props
}: React.HTMLAttributes<HTMLDivElement>) => (
  <div
    className={cn(
      "flex flex-col space-y-2 text-center sm:text-left",
      className
    )}
    {...props}
  />
)
SheetHeader.displayName = "SheetHeader"

/**
 * SheetFooter component provides a container for sheet action buttons.
 * 
 * @component
 * @param {React.HTMLAttributes<HTMLDivElement>} props - Standard div element props
 * @param {string} [props.className] - Additional CSS classes
 * @returns {JSX.Element} Sheet footer container
 */
const SheetFooter = ({
  className,
  ...props
}: React.HTMLAttributes<HTMLDivElement>) => (
  <div
    className={cn(
      "flex flex-col-reverse sm:flex-row sm:justify-end sm:space-x-2",
      className
    )}
    {...props}
  />
)
SheetFooter.displayName = "SheetFooter"

/**
 * SheetTitle component displays the sheet title.
 * Built on Radix UI Dialog primitive for accessibility.
 * 
 * @component
 * @param {React.ComponentPropsWithoutRef<typeof SheetPrimitive.Title>} props - Title props
 * @param {string} [props.className] - Additional CSS classes
 * @returns {JSX.Element} Sheet title element
 */
const SheetTitle = React.forwardRef<
  React.ElementRef<typeof SheetPrimitive.Title>,
  React.ComponentPropsWithoutRef<typeof SheetPrimitive.Title>
>(({ className, ...props }, ref) => (
  <SheetPrimitive.Title
    ref={ref}
    className={cn("text-lg font-semibold text-foreground", className)}
    {...props}
  />
))
SheetTitle.displayName = SheetPrimitive.Title.displayName

/**
 * SheetDescription component displays descriptive text below the sheet title.
 * Built on Radix UI Dialog primitive for accessibility.
 * 
 * @component
 * @param {React.ComponentPropsWithoutRef<typeof SheetPrimitive.Description>} props - Description props
 * @param {string} [props.className] - Additional CSS classes
 * @returns {JSX.Element} Sheet description element
 */
const SheetDescription = React.forwardRef<
  React.ElementRef<typeof SheetPrimitive.Description>,
  React.ComponentPropsWithoutRef<typeof SheetPrimitive.Description>
>(({ className, ...props }, ref) => (
  <SheetPrimitive.Description
    ref={ref}
    className={cn("text-sm text-muted-foreground", className)}
    {...props}
  />
))
SheetDescription.displayName = SheetPrimitive.Description.displayName

export {
  Sheet,
  SheetPortal,
  SheetOverlay,
  SheetTrigger,
  SheetClose,
  SheetContent,
  SheetHeader,
  SheetFooter,
  SheetTitle,
  SheetDescription,
}
